import React from "react";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { StationAggregate } from "../../../../types/analytics";
import { CHART_COLORS, CHART_FONT, sharedChartOptions, formatNumber } from "./chartConfig";

interface StationComparisonChartProps {
  stations: StationAggregate[];
}

const StationComparisonChart: React.FC<StationComparisonChartProps> = ({
  stations,
}) => {
  const hasMultiple = stations.length > 1;

  const chartData = hasMultiple
    ? {
        labels: stations.map((s) => s.stationId),
        datasets: [
          {
            label: "Revenue",
            data: stations.map((s) => s.revenue),
            backgroundColor: CHART_COLORS.primaryLight,
            borderColor: CHART_COLORS.primary,
            borderWidth: 1,
            borderRadius: 4,
          },
        ],
      }
    : null;

  const options = {
    ...sharedChartOptions,
    indexAxis: "y" as const,
    plugins: {
      ...sharedChartOptions.plugins,
      legend: { display: false },
      tooltip: {
        ...sharedChartOptions.plugins.tooltip,
        callbacks: {
          label: (ctx: any) => `${ctx.parsed.x.toFixed(2)} \u20AC`,
        },
      },
    },
    scales: {
      x: {
        ...sharedChartOptions.scales.y,
        ticks: {
          font: CHART_FONT,
          callback: (val: any) => `${val} \u20AC`,
        },
      },
      y: {
        ...sharedChartOptions.scales.x,
        ticks: { font: { ...CHART_FONT, size: 11 } },
      },
    },
  };

  const tableData = stations.map((s) => ({
    ...s,
    avgPerSession: s.sessions > 0 ? s.revenue / s.sessions : 0,
  }));

  // Add total row
  const totalRevenue = stations.reduce((s, st) => s + st.revenue, 0);
  const totalSessions = stations.reduce((s, st) => s + st.sessions, 0);
  const totalEnergy = stations.reduce((s, st) => s + st.energy, 0);

  return (
    <Card className="shadow-2 h-full" title="Station Comparison">
      {stations.length === 0 ? (
        <div className="flex flex-column align-items-center justify-content-center text-center p-5">
          <i className="pi pi-building text-4xl text-300 mb-3" />
          <p className="text-500">No station data available</p>
        </div>
      ) : (
        <>
          {hasMultiple && (
            <div className="mb-3">
              <Chart type="bar" data={chartData!} options={options} style={{ width: "100%" }} />
            </div>
          )}
          <DataTable value={tableData} size="small" stripedRows>
            <Column field="stationId" header="Station" />
            <Column
              field="sessions"
              header="Sessions"
              body={(row) => row.sessions}
              align="right"
            />
            <Column
              field="energy"
              header="kWh"
              body={(row) => formatNumber(row.energy)}
              align="right"
            />
            <Column
              field="revenue"
              header="Revenue"
              body={(row) => `${formatNumber(row.revenue)} \u20AC`}
              align="right"
            />
            <Column
              field="avgPerSession"
              header="Avg/Session"
              body={(row) => `${formatNumber(row.avgPerSession)} \u20AC`}
              align="right"
            />
          </DataTable>
          {hasMultiple && (
            <div className="flex justify-content-between p-2 font-bold text-sm border-top-1 surface-border mt-1">
              <span>Total</span>
              <div className="flex gap-5">
                <span>{totalSessions}</span>
                <span>{formatNumber(totalEnergy)} kWh</span>
                <span>{formatNumber(totalRevenue)} &euro;</span>
                <span>
                  {totalSessions > 0
                    ? formatNumber(totalRevenue / totalSessions)
                    : "0.00"}{" "}
                  &euro;
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default StationComparisonChart;
